import { Freeze } from "react-freeze";
import React, {
  useState,
  useRef,
  createContext,
  useMemo,
  useContext,
  useEffect,
} from "react";

interface DelayedFreezeProps {
  active: boolean;
  children: React.ReactNode;
  delay?: number;
}

const DelayedFreeze = ({
  active,
  children,
  delay = 2000,
}: DelayedFreezeProps) => {
  const [shouldFreeze, setShouldFreeze] = useState(!active);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (active) {
      setShouldFreeze(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } else {
      timeoutRef.current = setTimeout(() => {
        setShouldFreeze(true);
      }, delay);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [active, delay]);

  const freezing = !active;
  const contextValue = useMemo(() => {
    return {
      freezing,
    };
  }, [freezing]);

  const freeze = shouldFreeze && !active;

  return (
    <FreezeContext.Provider value={contextValue}>
      <Freeze freeze={freeze}>{children}</Freeze>
    </FreezeContext.Provider>
  );
};

export default DelayedFreeze;

const FreezeContext = createContext<{ freezing: boolean } | undefined>(
  undefined
);

export const useFreezeContext = () => {
  return useContext(FreezeContext) || { freezing: false };
};
